import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BASE_IMG_URL, commonImage, setPostImage } from '@images/imageUrls';
import TabFilter from '@components/tabFilter';
import Avatar from '@components/avatar';
import CommentInput from '@components/commentInput/pc';
import InfiniteScrollList from '@components/InfiniteScrollList';
import parse from 'html-react-parser';
import classNames from 'classnames';
import Toast from '@components/Toast';
import { formatTime } from '@utils/timeUtils';
import { Image } from 'antd';
import updateImageListSrc from "@utils/update-image-list-src";
import { replaceEmojiCodes } from '@utils/emoji';
import CommentBubble from '../commentBubble';

interface PCPostListProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    threadId: any;
    threadType: any;
    commentType?: number;
    filterList?: any;
    filterClick?: any;
    filterCurrentIndex?: any;
    postList?: any;
    hasMore?: boolean;
    loading?: boolean;
    onLoadMore?: () => void;
    onPostLike?: (data: any) => void;
    deletePopupStatus?: any;
    reportPopupStatus?: any;
    reportReasonList?: any;
    reportReason?: any;
    reportDescription?: any;
    handleSetState?: any;
    handleDeletPost?: Function;
    handleReportPost?: Function;
    onDeleteComment?: (itemId: any) => Promise<void>;
    onSinkComment?: (itemId: any, currentIsSink?: boolean) => Promise<void>;
    onStickyComment?: (itemId: any, currentIsSticky?: boolean) => Promise<void>;
}

interface PCPostListState {
    menuDropStatusIndex: any;
    selectedItemId?: any;
    selectedItemType?: any;
    replyingCommentId?: any;
    replyingCommentIndex?: any;
    replyingType?: 'post' | 'comment';
    replyingMainPostId?: any;
    commentInputVisible?: boolean;
    tempPostList?: any[];
    expandedReplies?: { [key: string]: { list: any[]; hasMore: boolean; lastId: number; loading: boolean } };
    lowQualityExpanded?: boolean;
    selectedCommentIsSink?: boolean;
    selectedCommentIsSticky?: boolean;
}

@inject('app', 'site', 'user', 'thread')
@observer
class PCPostList extends React.Component<PCPostListProps, PCPostListState> {
    state: PCPostListState = {
        menuDropStatusIndex: '',
        selectedItemId: '',
        selectedItemType: '',
        replyingCommentId: '',
        replyingCommentIndex: -1,
        replyingType: 'post',
        replyingMainPostId: '',
        commentInputVisible: false,
        tempPostList: [],
        expandedReplies: {},
        lowQualityExpanded: false,
        selectedCommentIsSink: false,
        selectedCommentIsSticky: false,
    };

    private replaceEmojiInContent = (content?: string) => replaceEmojiCodes(content || '', this.props.site?.emojiList || []);

    // 加载楼中楼评论
    handleLoadMoreReplies = async (mainPostId: any, lastCommentId?: any) => {
        const { thread } = this.props;
        if (!thread) return;

        const { expandedReplies = {} } = this.state;
        const current = expandedReplies[mainPostId] || { list: [], hasMore: true, lastId: 0, loading: false };
        if (current.loading || !current.hasMore) return;

        this.setState({ expandedReplies: { ...expandedReplies, [mainPostId]: { ...current, loading: true } } });

        try {
            const params: any = { 'page[size]': 5 };
            const queryLastId = lastCommentId || current.lastId;
            if (queryLastId) params['filter[idLt]'] = queryLastId;

            const res = await thread.getPostComments(mainPostId, params);
            if (res.success && res.data?.data) {
                const newList = res.data.data || [];
                const hasMore = res.data.page?.hasMore || false;

                const existingIds = new Set((current.list || []).map((i: any) => i?.id));
                const parentItem = (this.props.postList || []).find((p: any) => p?.id === mainPostId) || {};
                const lastThreeIds = new Set((parentItem.lastThreeComments || []).map((c: any) => c?.id));

                const filteredNewList = newList.filter((it: any) => {
                    const id = it?.id;
                    if (!id) return false;
                    if (existingIds.has(id)) return false;
                    if (lastThreeIds.has(id)) return false;
                    return true;
                });

                const mergedList = [...current.list, ...filteredNewList];
                const lastId = mergedList.length > 0 ? mergedList[mergedList.length - 1].id : current.lastId;

                this.setState({
                    expandedReplies: {
                        ...expandedReplies,
                        [mainPostId]: { list: mergedList, hasMore, lastId, loading: false },
                    },
                });
            } else {
                this.setState({ expandedReplies: { ...expandedReplies, [mainPostId]: { ...current, loading: false } } });
            }
        } catch (error) {
            console.error('加载楼中楼评论失败:', error);
            this.setState({ expandedReplies: { ...expandedReplies, [mainPostId]: { ...current, loading: false } } });
        }
    };

    toggleLowQuality = () => {
        this.setState({ lowQualityExpanded: !this.state.lowQualityExpanded });
    };

    handleShowCommentInput = () => {
        this.setState({ commentInputVisible: true });
    };

    handleHideCommentInput = () => {
        this.setState({ commentInputVisible: false });
    };

    handleReplyComment = (commentId: any, commentUser?: any, commentIndex: any = -1, mainPostId?: any) => {
        this.setState({
            replyingCommentId: commentId,
            replyingCommentIndex: commentIndex,
            replyingMainPostId: mainPostId || commentId,
            replyingType: 'comment',
        });
    };

    handleCancelReply = () => {
        this.setState({
            replyingCommentId: '',
            replyingCommentIndex: -1,
            replyingMainPostId: '',
            replyingType: 'post',
        });
    };

    handleSubmitMainComment = async (data: { content: string; images: any[]; newComment?: any }) => {
        try {
            this.handleHideCommentInput();
            this.setState((prev) => ({ tempPostList: [data.newComment, ...(prev.tempPostList || [])] }));
            this.props.thread?.prependPostToList?.(data.newComment);
        } catch (error) {
            Toast.info('评论失败');
        }
    };

    handleSubmitReply = async (data: { content: string; images: any[]; newComment?: any }) => {
        try {
            const { replyingCommentId, replyingCommentIndex, replyingMainPostId, tempPostList = [] } = this.state;
            const targetMainId = replyingMainPostId || replyingCommentId;

            this.setState({ tempPostList: this.applyReply(tempPostList, targetMainId, replyingCommentIndex, data.newComment) });
            this.props.thread?.appendReplyToPost?.({ mainPostId: targetMainId, newComment: data.newComment });
            this.handleCancelReply();
        } catch (error) {
            Toast.info('评论失败');
        }
    };

    private applyReply = (tempList: any[], mainId: any, nestedIndex: number, newComment: any) => {
        return (tempList || []).map((item: any) => {
            if ((item.id || item.tempId) !== mainId) return item;
            const lastThree = item.lastThreeComments || [];
            const updatedLastThree = nestedIndex >= 0 ? [...lastThree.slice(0, nestedIndex + 1), newComment, ...lastThree.slice(nestedIndex + 1)] : [newComment, ...lastThree];
            return { ...item, lastThreeComments: updatedLastThree, replyCount: (item.replyCount || 0) + 1 };
        });
    };

    handleFilterClick = (item: any, index: number) => {
        const { filterClick } = this.props;
        filterClick?.(item, index);
    };

    handleDeleteItem = async (itemId: any) => {
        if (this.props.onDeleteComment) {
            try {
                await this.props.onDeleteComment(itemId);
            } catch (error) {
                Toast.error({ content: '删除失败' });
            }
        }
    };

    handleSinkItem = async (itemId: any, currentIsSink?: boolean) => {
        if (this.props.onSinkComment) {
            try {
                await this.props.onSinkComment(itemId, currentIsSink);
            } catch (error) {
                Toast.error({ content: '沉贴失败' });
            }
        }
    };

    handleTopItem = async (itemId: any, currentIsSticky?: boolean) => {
        if (this.props.onStickyComment) {
            try {
                await this.props.onStickyComment(itemId, currentIsSticky);
            } catch (error) {
                Toast.error({ content: '置顶失败' });
            }
        }
    };

    handleLikeClick = (target: any, parentId?: any) => {
        if (!target) return;
        // 先记录原始状态用于接口调用
        const payload = { ...target };
        const nextLiked = !target.isLiked;
        const nextCount = Math.max(0, (target.likeCount || 0) + (nextLiked ? 1 : -1));

        // 乐观更新：顶层评论直接更新列表项；楼中楼在父评论的回复列表中更新
        if (!parentId || parentId === target.id) {
            this.props.thread?.listStore?.updateListItem?.({
                namespace: 'postList',
                id: target.id,
                updates: { isLiked: nextLiked, likeCount: nextCount },
            });
            this.setState((prev) => ({
                tempPostList: (prev.tempPostList || []).map((item) =>
                    (item.id || item.tempId) === target.id ? { ...item, isLiked: nextLiked, likeCount: nextCount } : item,
                ),
            }));
        } else {
            const { postList = [] } = this.props;
            const parent = postList.find((p: any) => p?.id === parentId);
            if (parent) {
                const patchedLastThree = (parent.lastThreeComments || []).map((c: any) =>
                    c?.id === target.id ? { ...c, isLiked: nextLiked, likeCount: nextCount } : c,
                );
                this.props.thread?.listStore?.updateListItem?.({
                    namespace: 'postList',
                    id: parentId,
                    updates: { lastThreeComments: patchedLastThree },
                });
            }

            this.setState((prev) => {
                const expanded = prev.expandedReplies?.[parentId];
                if (!expanded) return null as any;
                const patchedList = (expanded.list || []).map((c: any) =>
                    c?.id === target.id ? { ...c, isLiked: nextLiked, likeCount: nextCount } : c,
                );
                return {
                    expandedReplies: {
                        ...prev.expandedReplies,
                        [parentId]: { ...expanded, list: patchedList },
                    },
                } as any;
            });
        }

        this.props.onPostLike?.(payload);
    };

    handleCopyItem = (content: string) => {
        if (!content) {
            Toast.info({ content: '无内容可复制' });
            return;
        }
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                navigator.clipboard
                    .writeText(content)
                    .then(() => Toast.success({ content: '已复制' }))
                    .catch(() => this.fallbackCopy(content));
            } else {
                this.fallbackCopy(content);
            }
        } catch (error) {
            Toast.error({ content: '复制失败' });
        }
    };

    fallbackCopy = (text: string) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            Toast.success({ content: '已复制' });
        } catch (error) {
            Toast.error({ content: '复制失败' });
        }
        document.body.removeChild(textarea);
    };

    render() {
        const { thread, threadId, filterList, filterCurrentIndex, postList = [], user, hasMore, loading, onLoadMore } = this.props;
        const { menuDropStatusIndex, replyingCommentId, replyingCommentIndex, replyingMainPostId, commentInputVisible, tempPostList = [], expandedReplies = {}, lowQualityExpanded } = this.state;
        if (!thread) return null;
        const threadData: any = thread.threadData;

        const mergedMap = new Map();
        [...tempPostList, ...postList].forEach((item: any) => {
            if (!item) return;
            const id = item.id || `temp-${Math.random()}`;
            if (!mergedMap.has(id)) mergedMap.set(id, item);
        });
        const allComments = Array.from(mergedMap.values());
        const normalComments = allComments.filter((item) => !item.isSink);
        const lowQualityComments = allComments.filter((item) => item.isSink);
        const shouldCollapseLowQuality = allComments.length > 5 && lowQualityComments.length > 0;
        const hasServerCount = typeof threadData?.postCount === 'number' ? threadData.postCount > 0 : false;
        const hasComments = allComments.length > 0 || postList.length > 0 || hasServerCount;
        const hideEmpty = !!hasComments;

        const debugPostId = (() => {
            try {
                return new URLSearchParams(window.location.search).get('postId');
            } catch (e) {
                return null;
            }
        })();

        if (debugPostId) {
            const targetId = Number(debugPostId);
            const targetInAll = allComments.find((item: any) => (item?.id || item?._id) === targetId);
            const targetInNormalIndex = normalComments.findIndex((item: any) => (item?.id || item?._id) === targetId);
            const targetInLowQualityIndex = lowQualityComments.findIndex((item: any) => (item?.id || item?._id) === targetId);
            console.error('[threadDetail][render-order][pc]', {
                debugPostId,
                allCommentsCount: allComments.length,
                normalCommentsCount: normalComments.length,
                lowQualityCommentsCount: lowQualityComments.length,
                allCommentIds: allComments.slice(0, 20).map((item: any) => item?.id || item?._id),
                normalCommentIds: normalComments.slice(0, 20).map((item: any) => item?.id || item?._id),
                lowQualityCommentIds: lowQualityComments.slice(0, 20).map((item: any) => item?.id || item?._id),
                targetFound: !!targetInAll,
                targetIsSink: targetInAll?.isSink,
                targetInNormalIndex,
                targetInLowQualityIndex,
                shouldCollapseLowQuality,
            });
        }

        const renderCommentItem = (item: any, index: number) => {
            const expandedData = expandedReplies[item.id] || { list: [], hasMore: true, lastId: 0, loading: false };
            const displayedComments = [...(item.lastThreeComments || []), ...expandedData.list];
            const remainingCount = (item.replyCount || 0) - displayedComments.length;
            const showExpandButton = remainingCount > 0 && expandedData.hasMore;

            return (
                <div className={styles.postItemBox} key={item?.id || index}>
                    <div className={styles.postGap}></div>
                    <div className={classNames(styles.postItem, menuDropStatusIndex === `item-${index}` ? styles.menuDrop : '')}>
                        <Avatar avatarUrl={item.user?.avatar} size={40} userCode={item.user?.code} officialSize={16} titleMark={item.user?.titleMark} level={item.user?.level} levelBadgeWidth={18} levelBadgeHeight={12}></Avatar>
                        <div className={styles.itemDetail}>
                            <div className={styles.detailTopBox}>
                                <div className={styles.nicknameBox}>
                                    <div className={styles.nickname}>{item.user?.nickname}</div>
                                    <div className={styles.titleName}>{item.user?.titleName}</div>
                                    {threadData?.user?.code === item.user?.code && <div className={styles.masterText}>楼主</div>}
                                </div>
                                <div className={styles.postOperaBox}>
                                    <div className={styles.likedOpera} onClick={() => this.handleLikeClick(item)}>
                                        {!item.isLiked && <img src={`${BASE_IMG_URL}/common/like.png`} alt="" className={styles.icon} />}
                                        {item.isLiked && <img src={`${BASE_IMG_URL}/common/liked.png`} alt="" className={styles.icon} />}
                                        {item.likeCount != 0 && <div className={classNames(styles.likeText, item.isLiked ? styles.isLiked : '')}>{item.likeCount}</div>}
                                    </div>
                                    <div className={styles.commentText} onClick={() => window.globalEnsureLogin(() => this.handleReplyComment(item.id, item.user, -1))}>回复</div>
                                    <div
                                        className={styles.menuBox}
                                        onMouseEnter={() => {
                                            const currentMenuKey = `item-${index}`;
                                            this.setState({ menuDropStatusIndex: currentMenuKey, selectedItemId: item.id, selectedItemType: 'comment' });
                                            this.props.handleSetState?.({ selectedCommentId: item.id });
                                        }}
                                        onMouseLeave={() => {
                                            this.setState({ menuDropStatusIndex: '', selectedItemId: '', selectedItemType: '' });
                                            this.props.handleSetState?.({ selectedCommentId: '' });
                                        }}
                                    >
                                        <div className={styles.menuIcon}></div>
                                        <div className={styles.menuDropListBox}>
                                            <div
                                                className={styles.menuDropItem}
                                                onClick={() =>
                                                    window.globalEnsureLogin(() => {
                                                        this.setState({ selectedItemId: item.id, selectedItemType: 'comment' });
                                                        this.props.handleSetState?.({ selectedCommentId: item.id, reportCommentId: item.id, reportPopupStatus: true, reportType: 2 });
                                                    })
                                                }
                                            >
                                                举报
                                            </div>
                                            {item.permissions?.canDelete && (
                                                <div className={classNames(styles.menuDropItem, styles.delete)} onClick={() => window.globalEnsureLogin(() => this.handleDeleteItem(item.id))}>
                                                    删除
                                                </div>
                                            )}
                                            {item.permissions?.canSink && (
                                                <div className={styles.menuDropItem} onClick={() => this.handleSinkItem(item.id, item.isSink)}>
                                                    {item.isSink ? '取消沉贴' : '沉贴'}
                                                </div>
                                            )}
                                            {item.permissions?.canSticky && (
                                                <div className={styles.menuDropItem} onClick={() => this.handleTopItem(item.id, item.isSticky)}>
                                                    {item.isSticky ? '取消置顶' : '置顶'}
                                                </div>
                                            )}
                                            <div
                                                className={classNames(styles.menuDropItem)}
                                                onClick={() => {
                                                    const contentText = item.content ? item.content.replace(/<[^>]*>/g, '') : '';
                                                    this.handleCopyItem(contentText);
                                                }}
                                            >
                                                复制
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <CommentBubble decoration={item?.commentDecoration} platform="pc">
                                <div className={styles.richtext}>{parse(this.replaceEmojiInContent(item?.content || ''), { replace: (domNode: any) => {} })}</div>
                            </CommentBubble>
                            {item?.images?.length > 0 && (
                                <div className={styles.postImgListBox}>
                                    {item?.images.map((img: any, imgIndex: number) => (
                                        <Image key={img.id || imgIndex} style={{ width: '110px', height: '110px', objectFit: 'cover' }} src={img.webpUrl || updateImageListSrc(img.url, 12)} alt="basic" preview={{ mask: '预览' }} />
                                    ))}
                                </div>
                            )}
                            <div className={styles.postBottomInfo}>
                                <div className={styles.postTime}>{formatTime(item.createdAt)}</div>
                                {item.source && <div className={styles.model}>{item.source}</div>}
                                <div className={styles.model}>{item.ipRegion}</div>
                            </div>
                        </div>
                    </div>

                    {replyingMainPostId === item.id && replyingCommentIndex === -1 && (
                        <div className={styles.replyInputWrapper}>
                            <CommentInput type="reply" threadId={threadId} postId={item.id} replyToUser={item.user} onSubmit={this.handleSubmitReply} onCancel={this.handleCancelReply} commentType={this.props.commentType} />
                        </div>
                    )}

                    {displayedComments &&
                        displayedComments.length > 0 &&
                        displayedComments.map((comment: any, commentIndex: any) => (
                            <React.Fragment key={`nested-${comment.id || commentIndex}`}>
                                <div className={classNames(styles.postItem, styles.commentItem, menuDropStatusIndex === `comment-${index}-${commentIndex}` ? styles.menuDrop : '')} style={{paddingTop: commentIndex == 0 ? '14px' : '16px'}}>
                                    <Avatar avatarUrl={comment.user?.avatar} titleMark={comment.user?.titleMark} level={comment.user?.level} size={28} officialSize={14} userCode={comment.user?.code} levelBadgeWidth={18} levelBadgeHeight={12}></Avatar>
                                    <div className={styles.itemDetail}>
                                        <div className={styles.detailTopBox}>
                                            <div className={styles.nicknameBox}>
                                                <div className={styles.nickname}>{comment.user?.nickname}</div>
                                                <div className={styles.titleName}>{comment.user?.titleName}</div>
                                                {threadData?.user?.code === comment.user?.code && <div className={styles.masterText}>楼主</div>}
                                            </div>
                                            <div className={styles.postOperaBox}>
                                                <div className={styles.likedOpera} onClick={() => this.handleLikeClick(comment, item.id)}>
                                                    {!comment.isLiked && <img src={`${BASE_IMG_URL}/common/like.png`} alt="" className={styles.icon} />}
                                                    {comment.isLiked && <img src={`${BASE_IMG_URL}/common/liked.png`} alt="" className={styles.icon} />}
                                                    {comment.likeCount != 0 && <div className={classNames(styles.likeText, comment.isLiked ? styles.isLiked : '')}>{comment.likeCount}</div>}
                                                </div>
                                                <div className={styles.commentText} onClick={() => window.globalEnsureLogin(() => this.handleReplyComment(comment.id, comment.user, commentIndex, item.id))}>
                                                    回复
                                                </div>
                                                <div
                                                    className={styles.menuBox}
                                                    onMouseEnter={() => {
                                                        const currentMenuKey = `comment-${index}-${commentIndex}`;
                                                        this.setState({ menuDropStatusIndex: currentMenuKey, selectedItemId: comment.id, selectedItemType: 'comment' });
                                                        this.props.handleSetState?.({ selectedCommentId: comment.id });
                                                    }}
                                                    onMouseLeave={() => {
                                                        this.setState({ menuDropStatusIndex: '', selectedItemId: '', selectedItemType: '' });
                                                        this.props.handleSetState?.({ selectedCommentId: '' });
                                                    }}
                                                >
                                                    <div className={styles.menuIcon}></div>
                                                    <div className={styles.menuDropListBox}>
                                                        <div
                                                            className={styles.menuDropItem}
                                                            onClick={() =>
                                                                window.globalEnsureLogin(() => {
                                                                    this.setState({ selectedItemId: comment.id, selectedItemType: 'comment' });
                                                                    this.props.handleSetState?.({ selectedCommentId: comment.id, reportPopupStatus: true, reportType: 2 });
                                                                })
                                                            }
                                                        >
                                                            举报
                                                        </div>
                                                        {item.permissions?.canDelete && (
                                                            <div className={classNames(styles.menuDropItem, styles.delete)} onClick={() => this.handleDeleteItem(comment.id)}>
                                                                删除
                                                            </div>
                                                        )}
                                                        <div
                                                            className={classNames(styles.menuDropItem)}
                                                            onClick={() => {
                                                                const contentText = comment.content ? comment.content.replace(/<[^>]*>/g, '') : '';
                                                                this.handleCopyItem(contentText);
                                                            }}
                                                        >
                                                            复制
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <CommentBubble decoration={comment?.commentDecoration} platform="pc">
                                            <div className={styles.richtext} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline' }}>
                                                {comment?.commentUser?.nickname ? <span className={styles.replyPrefix} style={{ marginRight: 2 }}>回复 {comment.commentUser.nickname}：</span> : null}
                                                <span>{parse(this.replaceEmojiInContent(comment?.content || ''), { replace: (domNode: any) => {} })}</span>
                                            </div>
                                        </CommentBubble>
                                        {comment?.images?.length > 0 && (
                                            <div className={styles.postImgListBox}>
                                                {comment?.images.map((img: any, imgIndex: number) => (
                                                    <Image
                                                        key={img.id || imgIndex}
                                                        src={img.webpUrl || updateImageListSrc(img.url, 12)}
                                                        alt="basic"
                                                        style={{ width: '110px', height: '110px', objectFit: 'cover' }}
                                                        preview={{ mask: '预览' }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <div className={styles.postBottomInfo}>
                                            <div className={styles.postTime}>{formatTime(comment.createdAt)}</div>
                                            {comment.source && <div className={styles.model}>{comment.source}</div>}
                                            <div className={styles.model}>{comment.ipRegion}</div>
                                        </div>
                                    </div>
                                </div>
                                {replyingMainPostId === item.id && replyingCommentId === comment.id && replyingCommentIndex === commentIndex && (
                                    <div className={styles.nestedReplyInputWrapper}>
                                        <CommentInput
                                            type="reply"
                                            threadId={threadId}
                                            postId={item.id}
                                            commentPostId={comment.id}
                                            replyToUser={comment.user}
                                            onSubmit={this.handleSubmitReply}
                                            onCancel={this.handleCancelReply}
                                            commentType={this.props.commentType}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}

                    {showExpandButton && (
                        <div className={styles.expandRepliesBtn} onClick={() => {
                            const lastCommentId = displayedComments.length > 0 ? displayedComments[displayedComments.length - 1].id : 0;
                            this.handleLoadMoreReplies(item.id, lastCommentId);
                        }}>
                            {expandedData.loading ? '加载中...' : `查看全部${remainingCount}条回复`}
                            <img src={commonImage.arrowB2} alt="" className={styles.arrowIcon} />
                        </div>
                    )}
                </div>
            );
        };

        const hasLowQualitySection = lowQualityComments.length > 0;
        const listNoMoreText = hasLowQualitySection ? '' : '没有更多了';
        const showNoMoreAfterLowQuality = hasLowQualitySection && !loading && !hasMore && allComments.length > 0;

        return (
            <div className={styles.postListContainer}>
                <div className={styles.postListTitleBox}>
                    <div>全部评论 {threadData?.postCount}</div>
                    <TabFilter filterList={filterList} currentIndex={filterCurrentIndex} filterClick={(item: any, filterIndex: any) => this.handleFilterClick(item, filterIndex)}></TabFilter>
                </div>

                {!commentInputVisible && (
                    <div className={styles.commentEnterBox} onClick={() => window.globalEnsureLogin(() => this.handleShowCommentInput())}>
                        <Avatar avatarUrl={user?.avatar} size={50} userCode={user?.code} titleMark={user?.titleMark} level={user?.userInfo?.level}></Avatar>
                        <div className={styles.commentEnter}>
                            <div className={styles.placeholder}>分享脑洞</div>
                            <img src={`${BASE_IMG_URL}/common/setPost/photoBar.png`} alt="" className={styles.icon} />
                            <img src={`${BASE_IMG_URL}/common/setPost/expression.png`} alt="" className={styles.icon} />
                        </div>
                    </div>
                )}

                {commentInputVisible && (
                    <CommentInput type="post" threadId={threadId} commentType={this.props.commentType} onSubmit={this.handleSubmitMainComment} onCancel={this.handleHideCommentInput} />
                )}

                <InfiniteScrollList
                    variant="pc"
                    data={normalComments}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={onLoadMore || (() => {})}
                    renderItem={renderCommentItem}
                    emptyText={hideEmpty ? '' : '暂无评论'}
                    emptyIcon={hideEmpty ? '' : setPostImage.emptyPosts}
                    emptyActionText={hideEmpty ? '' : '抢先分享脑洞'}
                    onEmptyAction={this.handleShowCommentInput}
                    noMoreText={listNoMoreText}
                    className={styles.postList}
                />

                {shouldCollapseLowQuality && !lowQualityExpanded && (
                    <div className={styles.lowQualityToggle} onClick={this.toggleLowQuality}>
                        <span className={styles.foldedText}>已折叠 {lowQualityComments.length} 条相似评论</span>
                        <div className={styles.viewMoreBox}>
                            <span>查看更多</span>
                            <img src={commonImage.arrowB2} alt="" className={styles.arrowIcon} />
                        </div>
                    </div>
                )}

                {((shouldCollapseLowQuality && lowQualityExpanded) || (!shouldCollapseLowQuality && lowQualityComments.length > 0)) &&
                    lowQualityComments.map((item: any, index: any) => <React.Fragment key={`low-${item?.id || index}`}>{renderCommentItem(item, normalComments.length + index)}</React.Fragment>)}

                {showNoMoreAfterLowQuality && <div className={styles.noMoreText}>没有更多了</div>}
            </div>
        );
    }
}

export default PCPostList;
